import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Hash } from "lucide-react";

export default function AdminNumberingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ prefix: "", include_year: true, digits: 4 });
  const [seqEdits, setSeqEdits] = useState({});

  const load = () => api.get("/admin/numbering").then((r) => { setData(r.data); setForm(r.data.settings); });
  useEffect(() => { load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, []);

  if (user?.role !== "admin") return <div className="p-10 text-slate-500">Administration is restricted to admins.</div>;
  if (!data) return <div className="p-10 text-slate-500">Loading…</div>;

  const year = form.include_year ? new Date().getFullYear() : 0;
  const currentSeq = data.counters.find((c) => c.year === year)?.seq ?? 0;
  const preview = `${form.prefix}${form.include_year ? year : ""}${String(currentSeq + 1).padStart(form.digits, "0")}`;

  const saveSettings = async () => {
    try {
      const { data: d } = await api.put("/admin/numbering", { ...form, digits: Number(form.digits) });
      setData(d); toast.success("Numbering format saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveCounter = async (y) => {
    const seq = Number(seqEdits[y]);
    if (Number.isNaN(seq) || seq < 0) return toast.error("Sequence must be ≥ 0");
    try {
      const { data: d } = await api.put(`/admin/numbering/counters/${y}`, { seq });
      setData(d); setSeqEdits({ ...seqEdits, [y]: undefined }); toast.success(`Counter for ${y || "no-year"} set to ${seq}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const counters = data.counters.some((c) => c.year === year) ? data.counters : [{ year, seq: 0 }, ...data.counters];

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 space-y-6" data-testid="admin-numbering-page">
      <div>
        <p className="overline">Administration</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Project Numbering</h1>
        <p className="text-sm text-slate-500 mt-1">Configure how new project codes are generated. Existing project codes are never changed.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-slate-900 text-white grid place-items-center"><Hash className="h-5 w-5" /></div>
            <div><h2 className="text-base font-semibold text-slate-900">Format</h2><p className="text-xs text-slate-500">Prefix + optional year + zero-padded sequence</p></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Prefix</Label><Input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} placeholder="e.g. PF-" data-testid="numbering-prefix-input" /></div>
            <div><Label className="text-xs">Sequence digits</Label><Input type="number" min={2} max={8} value={form.digits} onChange={(e) => setForm({ ...form, digits: e.target.value })} data-testid="numbering-digits-input" /></div>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div><div className="text-sm font-medium text-slate-900">Include year</div><div className="text-xs text-slate-500">Sequence resets every year (YYYYNNNN)</div></div>
            <Switch checked={form.include_year} onCheckedChange={(v) => setForm({ ...form, include_year: v })} data-testid="numbering-include-year-switch" />
          </label>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <p className="overline">Next project code preview</p>
            <div className="mt-1 text-2xl font-mono font-bold text-slate-900" data-testid="numbering-preview">{preview}</div>
          </div>
          <Button onClick={saveSettings} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="numbering-save-btn">Save format</Button>
        </Card>

        <Card className="p-6 border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Sequence counters</h2>
          <p className="text-xs text-slate-500">Set or reset the last used sequence. The next project gets last + 1.</p>
          <div className="mt-4 divide-y divide-slate-100">
            {counters.map((c) => (
              <div key={c.year} className="py-3 flex items-center gap-3" data-testid={`counter-row-${c.year}`}>
                <div className="w-24 font-mono text-sm text-slate-900">{c.year || "No year"}</div>
                <div className="text-xs text-slate-500 w-24">Last: <span className="font-mono text-slate-800">{c.seq}</span></div>
                <Input type="number" min={0} className="h-9 w-32" placeholder="New value" value={seqEdits[c.year] ?? ""} onChange={(e) => setSeqEdits({ ...seqEdits, [c.year]: e.target.value })} data-testid={`counter-input-${c.year}`} />
                <Button size="sm" variant="outline" onClick={() => saveCounter(c.year)} disabled={seqEdits[c.year] === undefined || seqEdits[c.year] === ""} data-testid={`counter-save-${c.year}`}>Set</Button>
                <Button size="sm" variant="ghost" onClick={() => { setSeqEdits({ ...seqEdits, [c.year]: "0" }); }} data-testid={`counter-reset-${c.year}`}>Reset to 0</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
